/**
 * OBPHR2Service — Geocodificador L1.11 para patrimonio histórico no catalogado
 *
 * Busca bienes del OBPH (Otros Bienes del Patrimonio Histórico de Andalucía)
 * almacenados en R2 mediante fuzzy matching por nombre.
 *
 * Complementa al CGPHA: contiene bienes NO catalogados (no protegidos).
 * Solapamiento parcial con IAPH en bienes no protegidos.
 *
 * Datos: OBPH — ~8.305 features (672 municipios)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /obph/{ine5}.json.gz
 * Precisión: ±30 m (coordenadas OBPH aproximadas)
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-20
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  OBPHFeature,
  OBPHSearchOptions,
  OBPHResult,
} from '../../../types/obph';
import { OBPH_X_RANGE, OBPH_Y_RANGE, OBPH_PRECISION_M } from '../../../types/obph';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;

/** Configuración uFuzzy para nombres españoles de patrimonio */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 15,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: OBPHR2Service | null = null;

export function getOBPHR2Service(): OBPHR2Service {
  if (!serviceInstance) {
    serviceInstance = new OBPHR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetOBPHR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class OBPHR2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: OBPHFeature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<OBPHFeature[]>>();
  private readonly fuzzy: uFuzzy;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga features OBPH de un municipio desde R2 (con cache LRU) */
  async loadMunicipio(codINE: string): Promise<OBPHFeature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<OBPHFeature[]> {
    const url = `${this.baseUrl}/obph/${codINE}.json.gz`;
    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });
      if (response.status === 404) return [];
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: OBPHFeature[] = await response.json();
      return data.filter(f => this.isValidCoord(f));
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[OBPH] Timeout cargando ${codINE}`);
      } else {
        console.error(`[OBPH] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  async geocode(options: OBPHSearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name);
  }

  async search(options: OBPHSearchOptions): Promise<OBPHResult[]> {
    const { codINE, name, minConfidence = 60, maxResults = 5, caracter } = options;
    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    let candidates = features;
    if (caracter) {
      candidates = candidates.filter(f =>
        f.caracter.toLowerCase().includes(caracter.toLowerCase()),
      );
      if (candidates.length === 0) return [];
    }

    if (!name?.trim()) {
      return candidates.slice(0, maxResults).map(f => this.toResult(f, 70));
    }

    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: OBPHFeature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): OBPHResult[] {
    const haystack = candidates.map(f => f.tokens || f.nombre);
    const normalizedName = this.normalizeText(searchName);
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, , order] = searchResult;
    const processOrder = order ?? idxs;
    const results: OBPHResult[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  private calcFuzzyScore(needle: string, feature: OBPHFeature): number {
    const target = this.normalizeText(feature.tokens || feature.nombre);
    if (target === needle) return 1.0;
    if (target.includes(needle)) return 0.95;
    if (needle.includes(target)) return 0.90;
    const ratio = needle.length / Math.max(target.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 95;
    if (score >= 0.85) return 88;
    if (score >= 0.70) return 78;
    if (score >= 0.55) return 68;
    if (score >= 0.40) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN
  // ---------------------------------------------------------------------------

  private toResult(
    feature: OBPHFeature,
    confidence: number,
    fuzzyScore?: number,
  ): OBPHResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: OBPH_PRECISION_M,
      source: 'obph_r2',
      feature,
      fuzzyScore,
    };
  }

  toGeocodingResult(result: OBPHResult, originalQuery?: string): GeocodingResult {
    const f = result.feature;
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'obph_r2',
      sourceLayer: f.caracter,
      originalQuery,
      matchedName: f.nombre,
      matchType: result.fuzzyScore != null ? 'fuzzy' : 'filter',
      fuzzyScore: result.fuzzyScore,
      municipality: f.municipio,
      muniCode: f.cod_ine,
      province: f.provincia,
      metadata: {
        id: f.id,
        codigo: f.codigo,
        caracter: f.caracter,
        tipologia_gen: f.tipologia_gen,
        tipologia_esp: f.tipologia_esp,
        actividad: f.actividad,
        precision: result.precision,
        fuente: 'OBPH Otros Bienes del Patrimonio Histórico — IAPH / Junta de Andalucía',
      },
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private isValidCoord(f: OBPHFeature): boolean {
    return (
      f.x >= OBPH_X_RANGE[0] && f.x <= OBPH_X_RANGE[1] &&
      f.y >= OBPH_Y_RANGE[0] && f.y <= OBPH_Y_RANGE[1]
    );
  }

  private setCache(codINE: string, data: OBPHFeature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Carga datos directamente (para tests sin fetch) */
  loadFromData(codINE: string, features: OBPHFeature[]): void {
    const valid = features.filter(f => this.isValidCoord(f));
    this.cache.set(codINE, { data: valid, ts: Date.now() });
  }

  get cacheSize(): number { return this.cache.size; }
}

export default OBPHR2Service;
